import { Form } from 'antd';
import { useCallback, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useUpdateUserMutation } from '@app/api/User/api';
import {
  DATE_PICKER_FORMAT,
  ERROR,
  FRONT_WARNING,
  ISO_FORMAT_INDEX,
  SAVE_ADDITIONAL_EMPLOYEE_INFO,
  SUCCESS,
} from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { LeavePageContext, NotificationContext } from '@app/providers';
import { handleCatchError } from '@app/services';
import type { NEmployees, NUsers } from '@app/types';
import type { NUtils } from '@app/types/Utils';
import {
  afterUserUpdate,
  capitalizeKeys,
  keysFirstLetterToLower,
} from '@app/utils';

import { useEditEmployeeUnsavedChanges } from '@pages/Employees/EditEmployee/hooks/useEditEmployeeUnsavedChanges';

import type { NEditEmployee } from '@pages/Employees/EditEmployee/EditEmployee.types';

export const useEmployeeAdditionalInfoTab = (
  employee: NEmployees.EditEmployeeInfoTabProps['employee'],
  setEmployee: (props: NEmployees.EditEmployeeInfoTabProps['employee']) => void,
  setIsNeedToUpdateUserPreview: (value: boolean) => void
) => {
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [errorType, setErrorType] = useState<NUtils.ErrorTypes>(undefined);
  const [formData, setFormData] = useState(employee);
  const [isUpdated, setIsUpdated] = useState(false);

  const { userId, database, activeBranchId } = useUserPrint();
  const [updateUser] = useUpdateUserMutation();

  const [form] = Form.useForm();

  const navigate = useNavigate();

  const { showNotification } = useContext(NotificationContext);
  const { prepareStartedData } = useContext(LeavePageContext);

  const submitForm: NEditEmployee.SubmitForm = useCallback(
    async (
      {
        dateWorkStart,
        datePatentEnd,
        citizenship,
        passportInfo,
        taxPayerId,
        insuranceCertificateN,
      },
      user
    ) => {
      if (!user) {
        showNotification('Данные по пользователю не найдены', FRONT_WARNING);
        return false;
      }

      const data = {
        ...capitalizeKeys<NUsers.ModelUser>(user),
        DateWorkStart: dateWorkStart
          ? dateWorkStart.format(DATE_PICKER_FORMAT[ISO_FORMAT_INDEX])
          : null,
        DatePatentEnd: datePatentEnd
          ? datePatentEnd.format(DATE_PICKER_FORMAT[ISO_FORMAT_INDEX])
          : null,
        Citizenship: citizenship,
        PassportInfo: passportInfo,
        TaxPayerId: taxPayerId,
        InsuranceCertificateN: insuranceCertificateN,
      };

      if (!employee) {
        showNotification('Исполнитель не найден', FRONT_WARNING);
        return false;
      }

      setEmployee({
        ...employee,
        ...keysFirstLetterToLower(data),
      });

      if (userId && database && activeBranchId) {
        try {
          setLoading(true);
          await updateUser({
            data,
            ActiveBranchId: activeBranchId,
            logs: { isLogged: true, userId, database },
          }).unwrap();

          prepareStartedData({});
          setIsNeedToUpdateUserPreview(true);
          setIsUpdated(false);
          showNotification(
            'Дополнительная информация сотрудника обновлена успешно',
            SUCCESS
          );

          afterUserUpdate({
            id: data.Id,
            navigate,
            submitType: SAVE_ADDITIONAL_EMPLOYEE_INFO,
            isEditPage: true,
          });
          return true;
        } catch (error) {
          setErrorType(ERROR);
          setError(handleCatchError(error));
          return false;
        } finally {
          setLoading(false);
        }
      }

      return false;
    },
    [
      activeBranchId,
      database,
      employee,
      navigate,
      prepareStartedData,
      setEmployee,
      setIsNeedToUpdateUserPreview,
      showNotification,
      userId,
      updateUser,
    ]
  );

  const submitCurrentForm = useCallback(async () => {
    try {
      const values = await form.validateFields();

      return await submitForm(values, employee);
    } catch {
      return false;
    }
  }, [employee, form, submitForm]);

  const { handleUnsavedChanges } = useEditEmployeeUnsavedChanges({
    isUpdated,
    setIsUpdated,
    submitCurrentForm,
  });

  return {
    form,
    submitForm,
    isLoading,
    error,
    errorType,
    setError,
    setFormData,
    formData,
    isUpdated,
    setIsUpdated,
    handleUnsavedChanges,
  };
};
